import { downloadSheet, postForm, postJson } from "./http";

/** The chart-of-accounts import. */

/** What the diff decided about one row of the sheet. */
export type LedgerImportAction = "new" | "update" | "unchanged" | "invalid";

/**
 * How far a row got.
 *
 * `skipped` is not a failure and not a success: an unchanged row, an invalid
 * one, or a sub-account whose parent never got created. `failed` is Zoho saying
 * no, and only those are worth retrying.
 */
export type LedgerImportRowStatus =
  | "pending"
  | "created"
  | "updated"
  | "skipped"
  | "failed";

/**
 * Something wrong with a row, or worth knowing about it.
 *
 * `error` blocks that row and nothing else. `warning` blocks nothing — it is
 * the "you may be undoing somebody's change" class of thing, which is a
 * decision rather than a mistake.
 */
export interface LedgerImportIssue {
  /** The column heading, so the message points at a cell. */
  field: string;
  message: string;
  severity: "error" | "warning";
}

/** Field-level before and after, for the rows that change something. */
export type LedgerImportChanges = Record<string, { from: string | null; to: string | null }>;

export interface LedgerImportRowDTO {
  id: number;
  /** The line number in the CA's own spreadsheet, not our ordering. */
  sheet_row_no: number;
  zoho_account_id: string | null;
  account_name: string | null;
  account_type: string | null;
  account_type_label: string;
  parent_name: string | null;
  description: string | null;
  action: LedgerImportAction;
  status: LedgerImportRowStatus;
  depth: number;
  changes: LedgerImportChanges | null;
  issues: LedgerImportIssue[];
  error: string | null;
}

/**
 * Where the import as a whole has got to.
 *
 * `committing` covers both "a batch is running" and "a batch finished and there
 * is more to do" — the push is a series of round trips, and between them the
 * import sits here.
 */
export type LedgerImportStatus = "validated" | "committing" | "done" | "cancelled" | "failed";

export interface LedgerImportDTO {
  id: number;
  original_filename: string;
  status: LedgerImportStatus;
  total_rows: number;
  new_rows: number;
  changed_rows: number;
  unchanged_rows: number;
  invalid_rows: number;
  /** Outcomes. Zero until a push has run. */
  created_rows: number;
  updated_rows: number;
  failed_rows: number;
  created_at: string | null;
  committed_at: string | null;
  /**
   * This exact file has been pushed before. Not a block — re-running after a
   * partial failure is legitimate — but Zoho has no idempotency key, so a blind
   * re-push would create every new ledger on it a second time.
   */
  already_pushed_at: string | null;
}

/**
 * What one call to commit did.
 *
 * Present only on a commit response — a plain read of the review has no batch
 * to report on.
 */
export interface LedgerPushDTO {
  status: "committing" | "done";
  /**
   * The run stopped for a reason that will still be true if called again
   * straight away — a rate limit, or credentials Zoho refused. The caller must
   * NOT loop on this; it is a wait-and-retry, not a continue.
   */
  halted: boolean;
  halted_reason: string | null;
  /** Rows this batch attempted. */
  attempted: number;
  /** Rows still to go. */
  remaining: number;
  created: number;
  updated: number;
  failed: number;
}

/** `import` is null whenever there is nothing open, which is the ordinary state. */
export interface LedgerImportReviewDTO {
  import: LedgerImportDTO | null;
  rows: LedgerImportRowDTO[];
  push?: LedgerPushDTO;
}

interface ReviewEnvelope {
  success: boolean;
  message: string;
  data: LedgerImportReviewDTO;
}

/** The client's chart of accounts as a spreadsheet, saved to their machine. */
export const downloadLedgerTemplate = (publicId: string): Promise<void> =>
  downloadSheet("/api/onboarding/ledgers/template", { public_id: publicId }, "chart-of-accounts.xlsx");

/**
 * Send the filled-in sheet up and get back what it would change.
 *
 * The server puts the readable refusal under errors.file — "that sheet has no
 * header row" rather than "HTTP 400".
 */
export async function uploadLedgerSheet(
  publicId: string,
  file: File
): Promise<LedgerImportReviewDTO> {
  const form = new FormData();
  form.append("public_id", publicId);
  form.append("file", file);

  return (await postForm<ReviewEnvelope>("/api/onboarding/ledgers/import/upload", form, ["file"])).data;
}

/**
 * Where these endpoints put the sentence a CA should read.
 *
 * Without this, every refusal reached the screen as the envelope's generic
 * "Validation failed" — including "this sheet is part-way into the client's
 * books", which is the difference between a button that looks broken and one
 * that has told you why it will not do that.
 */
const REFUSAL_KEYS = ["public_id", "commit"];

/** The review as it stands. Survives a reload; returns `{import: null}` when there is none. */
export const fetchLedgerImport = async (publicId: string): Promise<LedgerImportReviewDTO> =>
  (await postJson<ReviewEnvelope>("/api/onboarding/ledgers/import/get", { public_id: publicId }, REFUSAL_KEYS)).data;

/** Throw the review away and delete the uploaded file. */
export const cancelLedgerImport = async (publicId: string): Promise<LedgerImportReviewDTO> =>
  (await postJson<ReviewEnvelope>("/api/onboarding/ledgers/import/cancel", { public_id: publicId }, REFUSAL_KEYS)).data;

/**
 * Send one batch of the reviewed sheet into the client's Zoho Books.
 *
 * THIS WRITES TO A CUSTOMER'S BOOKS AND NOTHING IT DOES CAN BE UNDONE. Zoho has
 * no bulk-create for accounts, so a batch is a handful of individual writes; the
 * caller keeps calling while `push.status` is `committing` — but stops on
 * `push.halted`, which means calling again now would just fail the same way.
 *
 * `acknowledgeDuplicate` corresponds to the CA having been shown that this exact
 * file was pushed before and choosing to send it anyway. The server refuses
 * without it, so it is a decision rather than a default.
 */
export const commitLedgerImport = async (
  publicId: string,
  acknowledgeDuplicate = false
): Promise<LedgerImportReviewDTO> =>
  (
    await postJson<ReviewEnvelope>(
      "/api/onboarding/ledgers/import/commit",
      { public_id: publicId, acknowledge_duplicate: acknowledgeDuplicate },
      REFUSAL_KEYS
    )
  ).data;

/** Dismiss a finished push. Not an undo — the ledgers are in Zoho. */
export const closeLedgerImport = async (publicId: string): Promise<LedgerImportReviewDTO> =>
  (await postJson<ReviewEnvelope>("/api/onboarding/ledgers/import/close", { public_id: publicId }, REFUSAL_KEYS)).data;
